import { cn } from '@neo/lib/utils'
import { useEffect, useRef } from 'react'
import { Radio } from './radio'
import {
  ACQUISITION_SOURCE_DETAIL_PLACEHOLDER,
  ACQUISITION_SOURCE_OPTIONS,
  type AcquisitionSource,
  type StepProps,
  stripUnsafeChars,
} from './wizard-data'

// Focused on mount: the field appears on the user's own click.
const OtherDetailInput = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return (
    <div className="mx-2 mb-2 rounded-md bg-bg3 p-2">
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={ACQUISITION_SOURCE_DETAIL_PLACEHOLDER}
        aria-label="Other source details"
        data-testid="onboarding-source-detail"
        // Borderless on the grey, so there is no second box inside the selected row.
        className="h-9 w-full rounded-lg border border-transparent bg-background px-3 text-foreground text-s-500 outline-none transition-colors placeholder:text-s-400 placeholder:text-tertiary focus-visible:border-st1"
      />
    </div>
  )
}

// Step 2. Same row anatomy as the step-1 cards; 'Other' expands in place with
// its free text on the grey inset panel.
export const StepSource = ({ state, update }: StepProps) => {
  const select = (value: AcquisitionSource) =>
    // Reselecting clears, as on step 1; leaving 'other' drops its text.
    update(
      state.acquisitionSource === value
        ? { acquisitionSource: '', acquisitionSourceDetail: '' }
        : { acquisitionSource: value, acquisitionSourceDetail: '' },
    )

  return (
    <div className="flex flex-col gap-3">
      {ACQUISITION_SOURCE_OPTIONS.map((option) => {
        const selected = state.acquisitionSource === option.value
        const expanded = selected && option.value === 'other'
        return (
          <div
            key={option.value}
            className={cn(
              'overflow-hidden rounded-lg border bg-background transition-colors',
              selected ? 'border-primary' : 'border-st2 hover:border-st1',
            )}
          >
            <button
              type="button"
              onClick={() => select(option.value)}
              aria-pressed={selected}
              aria-expanded={option.value === 'other' ? expanded : undefined}
              data-testid={`onboarding-source-${option.value}`}
              className={cn(
                'group flex w-full items-center gap-3 px-4 pt-3 text-left',
                expanded ? 'pb-2' : 'pb-3',
              )}
            >
              <span
                className={cn(
                  'flex-1 transition-colors',
                  selected
                    ? 'text-p-500 text-foreground'
                    : 'text-p-400 text-secondary group-hover:text-foreground',
                )}
              >
                {option.label}
              </span>
              <Radio selected={selected} />
            </button>
            {expanded && (
              <OtherDetailInput
                value={state.acquisitionSourceDetail}
                onChange={(value) => update({ acquisitionSourceDetail: stripUnsafeChars(value) })}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
